<script lang="ts" setup>
import { computed, PropType } from 'vue';
import { UserModel } from '../../models';
import {
  TirPmMultiselect,
  TirPmSelectBadge,
  TirPmIconView,
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  PopoverPositionEnum,
  TirPmScrollbar,
} from 'tir-components';
import { EmployeeMultiselectOptionModel } from './models';
import { PlusIcon, TrashIcon } from 'tir-style-system/icons/outline';

const emit = defineEmits<{
  /**Событие обновление выбранных сотрудников */
  (e: 'update:modelValue', value: Array<number>): void;
}>();

const props = defineProps({
  /**Выбранные сотрудники */
  modelValue: {
    type: Array as PropType<Array<number>>,
    default: [],
  },
  /**Сотрудники для выбора */
  employees: {
    type: Array as PropType<Array<UserModel>>,
    requred: true,
  },
  /**Ширина */
  width: {
    type: String,
    default: '100%',
  },
  /**Лейбл */
  label: {
    type: String,
  },
});

/**Опции для мультиселекта */
const options = computed(() =>
  props.employees.map(
    (employee) =>
      new EmployeeMultiselectOptionModel({
        Id: employee.Id,
        FirstName: employee.FirstName,
        LastName: employee.LastName,
        AvatarColor: employee.AvatarColor,
        ImageUrl: employee.ImageUrl,
      }),
  ),
);

/**Выбранные опции */
const selectedOptions = computed(() =>
  options.value.filter((option) => props.modelValue.some((employeeId) => option.Id === employeeId)),
);
/**Добавить юзера */
const selectEmployee = (employeeId: number) => {
  emit('update:modelValue', [...props.modelValue, employeeId]);
};
/**Удалить юзера */
const unSelectEmployee = (employeeId: number) => {
  emit(
    'update:modelValue',
    props.modelValue.filter((_employeeId) => employeeId !== _employeeId),
  );
};
</script>

<template>
  <div class="employee-multiselect">
    <TirPmMultiselect
      :popoverPosition="PopoverPositionEnum.Bottom"
      :options="options"
      :selected-options="selectedOptions"
      :label="label"
      width="100%"
      popoverWidth="32.5rem"
      class="employee-multiselect__select"
    >
      <template #target>
        <slot name="targer" />
      </template>
      <template #optionList="{ option }">
        <div
          style="display: flex; align-items: center; gap: 0.625rem; padding: 0.75rem"
          @click="selectEmployee(option.Id)"
        >
          <div style="width: 1.5rem; height: 1.5rem">
            <TirPmIconView
              :size="24"
              :isBordered="false"
              isRounded
              :colorCode="option.AvatarColor"
              :imageLoadUrl="option.ImageUrl"
            >
              <template #mainSlot>
                {{ option.FirstAndFirstLetterFullname }}
              </template>
            </TirPmIconView>
          </div>
          {{ option.FirstAndLastName }}
        </div>
      </template>
      <template #left>
        <slot name="left" />
      </template>
    </TirPmMultiselect>
    <TirPmScrollbar class="employee-multiselect__scrollbar" showOnHover>
      <div v-for="option in selectedOptions" class="employee-multiselect__selected-users">
        <div class="employee-multiselect__selected-users--data">
          <TirPmIconView
            :size="48"
            :isBordered="false"
            isRounded
            :colorCode="option.AvatarColor"
            :imageLoadUrl="option.ImageUrl"
          >
            <template #mainSlot>
              {{ option.FirstAndFirstLetterFullname }}
            </template>
          </TirPmIconView>
          <div>{{ option.LastName }} {{ option.FirstName[0] }}.</div>
        </div>
        <TirPmButton
          :variant="TirPmButtonVariantEnum.LinkState"
          :size="TirPmButtonSizeEnum.Regular"
          @click="unSelectEmployee(option.Id)"
        >
          <template #leftSlot>
            <TrashIcon />
          </template>
        </TirPmButton>
      </div>
    </TirPmScrollbar>
  </div>
</template>

<style scoped lang="scss">
.employee-multiselect {
  &__selected-users {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem 1.25rem;
    border-radius: 0.5rem;
    &:hover {
      background-color: var(--neutral-translucent-5, #272d370d);
    }
  }
  &__selected-users--data {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    font-size: 1rem;
    color: var(--text-primary, #272d37);
    font-weight: 400;
  }
  &__scrollbar {
    max-height: calc(100vh - 24.5rem);
  }
}
</style>

<style lang="scss">
.employee-multiselect {
  &__select {
    width: 100% !important;
    .tir-popover_element {
      justify-content: center;
      .button {
        width: 100% !important;
      }
    }
  }
}
</style>
